import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { CarBrand, CarModel, CarType } from '../types';
import { carBrandsAPI, carModelsAPI } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`car-tabpanel-${index}`}
      aria-labelledby={`car-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CarMasters: React.FC = () => {
  return (
      <CarMastersContent />
  );
};

const CarMastersContent: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  
  // Car Brands State
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [editingBrand, setEditingBrand] = useState<CarBrand | null>(null);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [brandFormData, setBrandFormData] = useState({ name: '', logo_url: '', status: true });

  // Car Models State
  const [openModelDialog, setOpenModelDialog] = useState(false);
  const [editingModel, setEditingModel] = useState<CarModel | null>(null);
  const [models, setModels] = useState<CarModel[]>([]);
  const [modelFormData, setModelFormData] = useState({ 
    brand_id: 0, 
    name: '', 
    car_type: 'Sedan' as CarType, 
    status: true 
  });
  
  const carTypes: CarType[] = ['Hatchback', 'SUV', 'Sedan', 'MUV', 'Minivan', 'Convertible', 'Other'];

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Fetch data on component mount
  useEffect(() => {
    fetchBrands();
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const data = await carBrandsAPI.getAll();
      setBrands(data.map((brand: any) => ({
        ...brand,
        status: Boolean(brand.status),
      })));
      setError(null);
    } catch (err) {
      setError('Failed to fetch car brands');
      showSnackbar('Failed to fetch car brands', 'error');
    } finally {
      setLoading(false);
    }
  };


  const fetchModels = async () => {
    try {
      const data = await carModelsAPI.getAll();
      console.log("🚀 Car Models API Response:", data); // 👈 check this
      setModels(data?.data || []); // adjust depending on API shape
    } catch (error) {
      console.error("Error fetching car models:", error);
    }
  };



  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Brand Handlers
  const handleOpenBrandDialog = (brand: CarBrand | null = null) => {
    if (brand) {
      setEditingBrand(brand);
      setBrandFormData({ name: brand.name, logo_url: brand.logo_url || '', status: brand.status });
    } else {
      setEditingBrand(null);
      setBrandFormData({ name: '', logo_url: '', status: true });
    }
    setOpenBrandDialog(true);
  };

  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setEditingBrand(null);
  };

  const handleBrandInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setBrandFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingBrand) {
        await carBrandsAPI.update(editingBrand.brand_id, brandFormData);
        showSnackbar('Brand updated successfully', 'success');
      } else {
        await carBrandsAPI.create(brandFormData);
        showSnackbar('Brand created successfully', 'success');
      }
      await fetchBrands();
      handleCloseBrandDialog();
    } catch (err) {
      showSnackbar(`Failed to ${editingBrand ? 'update' : 'create'} brand`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      try {
        setLoading(true);
        await carBrandsAPI.delete(id);
        showSnackbar('Brand deleted successfully', 'success');
        await fetchBrands();
      } catch (err) {
        showSnackbar('Failed to delete brand', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleBrandStatus = async (id: number) => {
    const brand = brands.find(b => b.brand_id === id);
    if (brand) {
      try {
        setLoading(true);
        await carBrandsAPI.update(id, {
          name: brand.name,
          logo_url: brand.logo_url,
          status: !brand.status,
        });
        showSnackbar('Brand status updated successfully', 'success');
        await fetchBrands();
      } catch (err) {
        showSnackbar('Failed to update brand status', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Model Handlers
  const handleOpenModelDialog = (model: CarModel | null = null) => {
    if (model) {
      setEditingModel(model);
      setModelFormData({ 
        brand_id: model.brand_id, 
        name: model.name, 
        car_type: model.car_type || 'Sedan',
        status: model.status 
      });
    } else {
      setEditingModel(null);
      setModelFormData({ 
        brand_id: brands[0]?.brand_id || 0, 
        name: '', 
        car_type: 'Sedan',
        status: true 
      });
    }
    setOpenModelDialog(true);
  };

  const handleCloseModelDialog = () => {
    setOpenModelDialog(false);
    setEditingModel(null);
  };

  const handleModelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setModelFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleModelSelectChange = (e: any) => {
    const { name, value } = e.target;
    setModelFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleModelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (editingModel) {
        await carModelsAPI.update(editingModel.model_id, modelFormData);
        showSnackbar('Model updated successfully', 'success');
      } else {
        await carModelsAPI.create(modelFormData);
        showSnackbar('Model created successfully', 'success');
      }
      await fetchModels();
      handleCloseModelDialog();
    } catch (err) {
      showSnackbar(`Failed to ${editingModel ? 'update' : 'create'} model`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModel = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this model?')) {
      try {
        setLoading(true);
        await carModelsAPI.delete(id);
        showSnackbar('Model deleted successfully', 'success');
        await fetchModels();
      } catch (err) {
        showSnackbar('Failed to delete model', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleModelStatus = async (id: number) => {
    const model = models.find(m => m.model_id === id);
    if (model) {
      try {
        setLoading(true);
        await carModelsAPI.update(id, {
          brand_id: model.brand_id,
          name: model.name,
          status: !model.status,
        });
        showSnackbar('Model status updated successfully', 'success');
        await fetchModels();
      } catch (err) {
        showSnackbar('Failed to update model status', 'error');
      } finally {
        setLoading(false);
      }
    }
  };
  const getBrandName = (brandId: number) => {
    return brands.find(b => b.brand_id === brandId)?.name || 'Unknown';
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>        
        <Typography variant="h4"  fontWeight="bold">Car Masters Management</Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 0 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Car Brands" />
          <Tab label="Car Models" />
        </Tabs>
      </Paper>

      {/* Loading Overlay */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Car Brands Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ p: 0 }}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
            p: 0
          }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>Car Brands</Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenBrandDialog()}
              disabled={loading}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Add Brand
            </Button>
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Brand Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Logo URL</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brands.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        No car brands found. Click "Add Brand" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  brands.map((brand) => (
                    <TableRow 
                      key={brand.brand_id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>{brand.brand_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {brand.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                          {brand.logo_url || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={brand.status ? 'Active' : 'Inactive'}
                          color={brand.status ? 'success' : 'default'}
                          size="small"
                          onClick={() => toggleBrandStatus(brand.brand_id)}
                          sx={{ cursor: 'pointer', minWidth: 80 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          onClick={() => handleOpenBrandDialog(brand)} 
                          color="primary" 
                          size="small"
                          disabled={loading}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteBrand(brand.brand_id)} 
                          color="error" 
                          size="small"
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        </Box>
      </TabPanel>

      {/* Car Models Tab */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
            p: 0
          }}>
            <Box>
              <Typography variant="h6" fontWeight={600}>Car Models</Typography>
              
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModelDialog()}
              disabled={loading || brands.length === 0}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Add Model
            </Button>
          </Box>

          {brands.length === 0 ? (
            <Alert severity="info">
              Please add at least one car brand before creating models.
            </Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Model Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No car models found. Click "Add Model" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    models.map((model: CarModel) => (
                      <TableRow 
                        key={model.model_id}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>{model.model_id}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getBrandName(model.brand_id)} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {model.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={model.car_type || 'N/A'} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={model.status ? 'Active' : 'Inactive'}
                            color={model.status ? 'success' : 'default'}
                            size="small"
                            onClick={() => toggleModelStatus(model.model_id)}
                            sx={{ cursor: 'pointer', minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            onClick={() => handleOpenModelDialog(model)} 
                            color="primary" 
                            size="small"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDeleteModel(model.model_id)} 
                            color="error" 
                            size="small"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </TabPanel>

      {/* Brand Dialog */}
      <Dialog 
        open={openBrandDialog} 
        onClose={handleCloseBrandDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editingBrand ? 'Edit Brand' : 'Add New Brand'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {editingBrand ? 'Update the car brand details' : 'Create a new car brand'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleBrandSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Brand Name"
              name="name"
              value={brandFormData.name}
              onChange={handleBrandInputChange}
              margin="normal"
              required
              variant="outlined"
              placeholder="e.g., Toyota, Honda"
            />
            <TextField
              fullWidth
              label="Logo URL"
              name="logo_url"
              value={brandFormData.logo_url}
              onChange={handleBrandInputChange}
              margin="normal"
              variant="outlined"
              placeholder="https://example.com/logo.png"
              helperText="Optional: Enter the URL for the brand logo"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCloseBrandDialog}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {editingBrand ? 'Update' : 'Add'} Brand
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Model Dialog */}
      <Dialog 
        open={openModelDialog} 
        onClose={handleCloseModelDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {editingModel ? 'Edit Model' : 'Add New Model'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {editingModel ? 'Update the car model details' : 'Create a new car model'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleModelSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>Brand *</InputLabel>
              <Select
                name="brand_id"
                value={modelFormData.brand_id}
                label="Brand *"
                onChange={handleModelSelectChange}
                required
              >
                {brands.filter(b => b.status).map((brand) => (
                  <MenuItem key={brand.brand_id} value={brand.brand_id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Model Name"
              name="name"
              value={modelFormData.name}
              onChange={handleModelInputChange}
              margin="normal"
              required
              variant="outlined"
              placeholder="e.g., Camry, Civic"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>Car Type *</InputLabel>
              <Select
                name="car_type"
                value={modelFormData.car_type}
                label="Car Type *"
                onChange={handleModelSelectChange}
                required
              >
                {carTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button 
              onClick={handleCloseModelDialog}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {editingModel ? 'Update' : 'Add'} Model
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CarMasters;
